import { computed, ref } from "vue";

import uniqBy from "lodash/uniqBy";
import { defineStore } from "pinia";

import type { EmailsResponse } from "@/models/applications";
import type { SendApplicationEmailModalData } from "@/models/emails";
import type { MultiSelectOption } from "@/models/options";

const getDefaultModalData = (): SendApplicationEmailModalData => ({
  applications: [],
  customizations: [],
  email_addresses: [],
  templates: [],
  reply_to_recipients: [],
});

const useEmailsStore = defineStore("emails", () => {
  const modalData = ref<SendApplicationEmailModalData>(getDefaultModalData());
  const applicationEmails = ref<EmailsResponse>();

  const recipientApplicationsOptions = computed(() =>
    modalData.value?.applications?.flatMap(
      ({ owners }) =>
        owners?.map((owner) => ({
          id: String(owner.personal_info_id),
          name: owner.email_address,
        })) ?? []
    )
  );

  const recipientReplyToOptions = computed<MultiSelectOption[]>(
    () =>
      modalData.value?.reply_to_recipients?.map((item) => ({
        id: String(item.personal_info_id),
        name: item.email_address,
      })) ?? []
  );

  const dealEmailsOptions = computed<MultiSelectOption[]>(
    () =>
      applicationEmails.value?.owners.flatMap((owner) =>
        owner.all_emails.map((emailData) => ({
          id: String(owner.id),
          name: emailData.email,
        }))
      ) ?? []
  );

  const recipientOptions = computed<MultiSelectOption[]>(() => {
    const allEmails = [
      ...recipientApplicationsOptions.value,
      ...recipientReplyToOptions.value,
      ...dealEmailsOptions.value,
    ];
    return uniqBy(
      allEmails.map((email) => ({
        name: email.name,
        id: `${email.id}|${email.name}`,
      })),
      "id"
    );
  });

  return {
    modalData,
    recipientOptions,
    recipientApplicationsOptions,
    applicationEmails,
  };
});

export default useEmailsStore;
